import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { PickeoEvent, SalesOrder, SyncQueueItem } from '@/types';

/**
 * Esquema de IndexedDB para el modo offline.
 * - `orders`: snapshot de los pedidos asignados (para trabajar sin red).
 * - `events`: pickeo_events generados localmente, pendientes de sync.
 * - `queue`:  cola genérica de mutaciones pendientes (events, fulfillments, etc.).
 * - `meta`:   última fecha de sync, device_id, etc.
 */

interface FoxboxDepDB extends DBSchema {
  orders: {
    key: string; // sales_order_id
    value: SalesOrder & { _cachedAt: string };
    indexes: { 'by-status': string; 'by-assigned': string };
  };
  events: {
    key: string; // _localId
    value: PickeoEvent;
    indexes: { 'by-order': string; 'by-synced': string };
  };
  queue: {
    key: string;
    value: SyncQueueItem;
    indexes: { 'by-kind': string };
  };
  meta: {
    key: string;
    value: { key: string; value: unknown; updatedAt: string };
  };
}

const DB_NAME = 'foxbox-deposito';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<FoxboxDepDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<FoxboxDepDB>> {
  if (!dbPromise) {
    dbPromise = openDB<FoxboxDepDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('orders')) {
          const orders = db.createObjectStore('orders', { keyPath: 'id' });
          orders.createIndex('by-status', 'status');
          orders.createIndex('by-assigned', 'assigned_to');
        }
        if (!db.objectStoreNames.contains('events')) {
          const events = db.createObjectStore('events', { keyPath: '_localId' });
          events.createIndex('by-order', 'sales_order_id');
          events.createIndex('by-synced', 'synced_at');
        }
        if (!db.objectStoreNames.contains('queue')) {
          const queue = db.createObjectStore('queue', { keyPath: 'id' });
          queue.createIndex('by-kind', 'kind');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

export async function saveOrdersSnapshot(orders: SalesOrder[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('orders', 'readwrite');
  const cachedAt = new Date().toISOString();
  // IndexedDB usa structured-clone y NO puede clonar los proxies reactivos de
  // Vue (ni objetos anidados con getters). Serializamos a objeto plano antes.
  const plain: SalesOrder[] = JSON.parse(JSON.stringify(orders));
  await Promise.all(plain.map((o) => tx.store.put({ ...o, _cachedAt: cachedAt })));
  await tx.done;
  await setMeta('orders.lastSync', cachedAt);
}

export async function getCachedOrders(): Promise<SalesOrder[]> {
  const db = await getDB();
  const all = await db.getAll('orders');
  return all.map(({ _cachedAt: _drop, ...rest }) => rest);
}

export async function enqueueEvent(ev: PickeoEvent): Promise<void> {
  const db = await getDB();
  const localId = ev._localId ?? crypto.randomUUID();
  await db.put('events', { ...ev, _localId: localId });
  await db.put('queue', {
    id: localId,
    kind: 'pickeo_event',
    payload: ev,
    attempts: 0,
    enqueued_at: new Date().toISOString(),
  });
}

export async function listPendingQueue(): Promise<SyncQueueItem[]> {
  const db = await getDB();
  return db.getAll('queue');
}

export async function markSynced(localId: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['events', 'queue'], 'readwrite');
  const ev = await tx.objectStore('events').get(localId);
  if (ev) {
    ev.synced_at = new Date().toISOString();
    await tx.objectStore('events').put(ev);
  }
  await tx.objectStore('queue').delete(localId);
  await tx.done;
}

export async function bumpAttempt(localId: string, errorMessage: string): Promise<void> {
  const db = await getDB();
  const item = await db.get('queue', localId);
  if (!item) return;
  await db.put('queue', {
    ...item,
    attempts: item.attempts + 1,
    last_error: errorMessage,
  });
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put('meta', { key, value, updatedAt: new Date().toISOString() });
}

export async function getMeta<T = unknown>(key: string): Promise<T | undefined> {
  const db = await getDB();
  const row = await db.get('meta', key);
  return row?.value as T | undefined;
}

/** Devuelve un device_id estable por instalación (para auditoría). */
export async function getDeviceId(): Promise<string> {
  let id = await getMeta<string>('device.id');
  if (!id) {
    id = crypto.randomUUID();
    await setMeta('device.id', id);
  }
  return id;
}
